<template>
  <!-- Date-of-birth filter. Three independent selectors (day / month /
       year) plus a tri-state "has DOB?" radio. Value shape unchanged:
         { day: number|null, month: number|null (0-indexed),
           year: number|null, hasDob: 'true'|'false'|null }
       so the existing `matches()` and validator keep working. -->
  <!-- has-DOB tri-state. `null` = no filter on whether DOB is recorded,
       `'true'` = only customers with a DOB (drills into day/month/year
       below), `'false'` = only customers WITHOUT a DOB recorded. -->
  <SegmentField
    :model-value="hasDobValue"
    :options="hasDobOptions"
    @update:model-value="(v: any) => onHasDobChange(v)"
  />

  <!-- Drill-down. Only meaningful when filtering "has DOB", and all
       three selectors are independent — leave any blank to mean "any". -->
  <div v-if="showParts" class="mt-2 space-y-2">
    <div class="grid grid-cols-3 gap-2">
      <USelect
        :model-value="modelValue.day ?? undefined"
        :items="dayOptions"
        value-key="value"
        :placeholder="$t('datetime.labels.day')"
        @update:model-value="(v: any) => onPartChange('day', v)"
      />
      <USelect
        :model-value="modelValue.month ?? undefined"
        :items="monthOptions"
        value-key="value"
        :placeholder="$t('datetime.labels.month')"
        @update:model-value="(v: any) => onPartChange('month', v)"
      />
      <USelect
        :model-value="modelValue.year ?? undefined"
        :items="yearOptions"
        value-key="value"
        :placeholder="$t('datetime.labels.year')"
        @update:model-value="(v: any) => onPartChange('year', v)"
      />
    </div>
    <button
      v-if="modelValue.day !== null || modelValue.month !== null || modelValue.year !== null"
      type="button"
      class="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      @click="clearParts"
    >
      {{ $t('general.clear') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { SegmentField } from '@/components/fields'

const { t } = useI18n()

const modelValue = defineModel<{
  day: number | null
  month: number | null
  year: number | null
  hasDob: 'true' | 'false' | null
}>({
  default: () => ({ day: null, month: null, year: null, hasDob: null }),
})

// hasDob's null state isn't representable in URadioGroup (the value must
// be one of the option values). We surface 'all' as the "no filter" choice
// and translate to/from null at the boundaries.
const hasDobValue = computed(() => modelValue.value.hasDob ?? 'all')

const hasDobOptions = computed(() => [
  { value: 'all', label: t('general.all') },
  { value: 'true', label: t('components.customer.list.dateOfBirth.hasDOB') },
  { value: 'false', label: t('components.customer.list.dateOfBirth.hasNoDOB') },
])

const hasDateParts = computed(() => modelValue.value.day !== null || modelValue.value.month !== null || modelValue.value.year !== null)
const showParts = computed(() => modelValue.value.hasDob === 'true' || hasDateParts.value)

const onHasDobChange = (v: string) => {
  modelValue.value = {
    ...modelValue.value,
    hasDob: v === 'all' ? null : (v as 'true' | 'false'),
    // When the user picks "no DOB", a day/month/year drill-down is
    // meaningless — clear it so the filter doesn't carry stale values
    // that wouldn't render anyway.
    ...(v === 'false' ? { day: null, month: null, year: null } : {}),
  }
}

// USelect doesn't differentiate "value: 0" from "value: null" — January
// (`month: 0`) collides with the placeholder otherwise. We store the
// numeric value but display labels separately.
const dayOptions = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
)

const monthOptions = computed(() => {
  const months = [
    'datetime.months.january', 'datetime.months.february', 'datetime.months.march',
    'datetime.months.april', 'datetime.months.may', 'datetime.months.june',
    'datetime.months.july', 'datetime.months.august', 'datetime.months.september',
    'datetime.months.october', 'datetime.months.november', 'datetime.months.december',
  ]
  return months.map((key, idx) => ({ value: idx, label: t(key) }))
})

const yearOptions = computed(() => {
  // Customer-meaningful range: ~120 years back from this year. Newest
  // first so users typing recent birth years find them at the top.
  const currentYear = new Date().getFullYear()
  const years: { value: number; label: string }[] = []
  for (let y = currentYear; y >= currentYear - 120; y--) {
    years.push({ value: y, label: String(y) })
  }
  return years
})

const onPartChange = (part: 'day' | 'month' | 'year', value: number | null) => {
  modelValue.value = { ...modelValue.value, hasDob: 'true', [part]: value }
}

const clearParts = () => {
  modelValue.value = { ...modelValue.value, day: null, month: null, year: null }
}
</script>
